import cloneDeep from "lodash-es/cloneDeep";
import { QueryClient } from "@tanstack/react-query";
import { IReviewTree, IWashoutData } from "@/Washouts/types/WashoutReview";
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";
import { IWashoutApprovedByPositionItem } from "@/Washouts/types/PayrollClosing/CommissionCycleClosingReviewers";

type TResult = {
	position: string;
	status: string;
	userId: string;
	commissionCycleId?: string;
	payPeriodId?: string;
	review: { status: string };
	employee: { _id: string; position: string };
};

const getStatusPosition = (position: string, result: TResult[]) => result.find((item) => item.position === position)?.status;

export const commissionCycleSubscriptionCb = ({
	queryClient,
	locationId,
	payPeriodId,
	result,
}: {
	queryClient: QueryClient;
	locationId: string;
	payPeriodId: string;
	result: TResult[];
}) => {
	const { payPeriodId: incomingPayPeriodId, commissionCycleId } = result[0] ?? {};
	queryClient.setQueriesData<ICommissionCycle[]>({ queryKey: ["commissionCycles", locationId] }, (cachedData = []) => {
		return cachedData.map((cycle) => {
			if (cycle._id !== commissionCycleId) {
				return cycle;
			}

			const updatedPayPeriods = cycle.payPeriods.map((period) => {
				if (period._id !== incomingPayPeriodId) {
					return period;
				}

				const positionStatusUpdated = period.positionStatus.map((item) => {
					const incomingStatus = getStatusPosition(item.position, result);
					return {
						...item,
						status: incomingStatus ?? item.status,
					};
				});

				return {
					...period,
					positionStatus: positionStatusUpdated,
				};
			});
			return {
				...cycle,
				payPeriods: updatedPayPeriods,
			};
		});
	});

	queryClient.setQueriesData<IWashoutApprovedByPositionItem[]>(
		{ queryKey: ["washoutApprovedByPosition", locationId, payPeriodId] },
		(cachedData = []) => {
			return cachedData.map((item) => {
				const updatedStatus = result.find((res) => res.position === item.position);
				if (!updatedStatus) {
					return item;
				}
				const userIds =
					updatedStatus.review.status === "APPROVED"
						? [...item.userIds, updatedStatus.userId]
						: item.userIds.filter((id) => id !== updatedStatus.userId);
				return {
					...item,
					userIds,
				};
			});
		}
	);
};

export const updateWashoutReviewTreeCb = ({
	locationId,
	payPeriodId,
	queryClient,
	result,
}: {
	queryClient: QueryClient;
	locationId: string;
	payPeriodId: string;
	result: TResult;
}) => {
	queryClient.setQueriesData<IReviewTree[]>({ queryKey: ["washoutReview", "tree", locationId, payPeriodId] }, (oldData = []) => {
		const { position, userId, commissionCycleId, review } = result;
		const resultReviewed = review?.status === "APPROVED";
		const newData = cloneDeep(oldData);
		const foundPosition = newData?.find((item) => item.title === position);
		const foundPositionIndex = newData?.findIndex((item) => item.title === position);

		if (!foundPosition) {
			return oldData;
		}

		const foundWashout = foundPosition?.list?.find((washout: { userId: string }) => washout.userId === userId);
		if (!foundWashout) {
			return oldData;
		}

		const hasDeeperLevel = Boolean(foundWashout?.list?.length);

		if (hasDeeperLevel) {
			const foundRelativeCycle = foundWashout?.list?.find(
				(washout: { commissionCycleId: string }) => washout.commissionCycleId === commissionCycleId
			);
			if (!foundRelativeCycle) {
				return oldData;
			}
			foundRelativeCycle.reviewed = resultReviewed;
		} else {
			foundWashout.reviewed = resultReviewed;
			if (foundPositionIndex === undefined || foundPositionIndex === -1) {
				return oldData;
			}
			newData[foundPositionIndex] = foundPosition;
		}

		return newData;
	});
};

export const updateWashoutModelCb = ({
	result,
	queryClient,
	commissionCycleId,
	payPeriodId,
}: {
	commissionCycleId: string;
	payPeriodId: string;
	queryClient: QueryClient;
	result: TResult;
}) => {
	const { position, userId, review } = result;
	queryClient.setQueriesData({ queryKey: ["getWashout", payPeriodId, position, userId, commissionCycleId] }, (oldData?: IWashoutData) => {
		if (oldData?.employee._id !== userId) {
			return oldData;
		}
		return {
			...oldData,
			review,
		};
	});
};
